import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import {
  selectActiveAssets,
  selectActiveCapitalProtection,
  selectActiveIssuers,
  selectActiveLayers,
  selectActiveMaturities,
  selectActivePayouts,
  selectActiveShariaa,
  selectActiveUnderlyingAsset,
  selectMarket,
  selectMarketName,
  selectSelectedSmartfolioId,
  setMarketName,
  setMarketState,
  setSmartfolioId,
  toggleCapitalProtection,
  toggleClass,
  toggleIssuerRating,
  toggleLayer,
  toggleMaturity,
  togglePayoutStructure,
  toggleShariaa,
  toggleUnderlyingAsset,
} from "src/store/Markets/MarketSlice";
import { useGetRecommendedSmartfoliosQuery } from "src/store/middleware/api_v2";

/**
 * The market list keeps what the user picked in the URL:
 *
 *   /smartfolios?market=2&layer=Thematic&class=Global&sf=30
 *
 * The store is still the working state — every filter component reads and
 * writes it as before — but the URL is what survives leaving the page. Coming
 * back, reloading and sharing a link all restore the same list, and "All" is a
 * value in the URL (market=all) rather than the absence of one, which is what
 * made it indistinguishable from "never picked anything".
 */
export const ALL_MARKETS = "all";
export const MARKET_PARAM = "market";

/** Where to land when we have no idea what list the user was looking at. */
export const ALL_MARKETS_URL = `/smartfolios?${MARKET_PARAM}=${ALL_MARKETS}`;

const GROUPS = [
  { param: "layer", toggle: toggleLayer },
  { param: "class", toggle: toggleClass },
  { param: "issuer", toggle: toggleIssuerRating },
  { param: "payout", toggle: togglePayoutStructure },
  { param: "maturity", toggle: toggleMaturity },
  { param: "shariaa", toggle: toggleShariaa },
  { param: "capital", toggle: toggleCapitalProtection },
  { param: "underlying", toggle: toggleUnderlyingAsset },
];

const buildSearch = (
  market: string,
  activeGroups: string[][],
  smartfolioId: number,
) => {
  const params = new URLSearchParams();
  params.set(MARKET_PARAM, market || ALL_MARKETS);
  GROUPS.forEach(({ param }, index) => {
    activeGroups[index]?.forEach((value) => params.append(param, value));
  });
  if (smartfolioId) params.set("sf", String(smartfolioId));
  return `?${params.toString()}`;
};

const findMarketName = (products: any[], market: string) => {
  const id = Number(market);
  for (const product of products || []) {
    const found = product?.object_content?.classification_content?.markets?.find(
      (candidate) => candidate?.id === id,
    );
    if (found?.name) return found.name;
  }
  return undefined;
};

export const useMarketUrlState = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const market = useSelector(selectMarket);
  const marketName = useSelector(selectMarketName);
  const smartfolioId = useSelector(selectSelectedSmartfolioId);

  const activeGroups = [
    useSelector(selectActiveLayers),
    useSelector(selectActiveAssets),
    useSelector(selectActiveIssuers),
    useSelector(selectActivePayouts),
    useSelector(selectActiveMaturities),
    useSelector(selectActiveShariaa),
    useSelector(selectActiveCapitalProtection),
    useSelector(selectActiveUnderlyingAsset),
  ];

  const [isHydrated, setIsHydrated] = useState(false);
  const search = buildSearch(market, activeGroups, smartfolioId);

  // URL -> store. Once per mount, which is also once per navigation back here.
  // setMarketState clears every filter group, so this doubles as the reset of
  // whatever the previous visit left in the store.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlMarket = params.get(MARKET_PARAM);

    dispatch(
      setMarketState(
        !urlMarket || urlMarket === ALL_MARKETS ? undefined : urlMarket,
      ),
    );
    GROUPS.forEach(({ param, toggle }) => {
      // deduped: these are toggles, so a hand-edited ?layer=X&layer=X would
      // switch the filter on and straight back off again
      new Set(params.getAll(param)).forEach((value) => dispatch(toggle(value)));
    });
    const urlSmartfolioId = Number(params.get("sf"));
    dispatch(setSmartfolioId(urlSmartfolioId > 0 ? urlSmartfolioId : 0));

    setIsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // store -> URL. replace(), not push(), so that ticking filters does not fill
  // the back stack with one entry per click.
  useEffect(() => {
    if (!isHydrated) return;
    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, search]);

  // The URL carries the market id; the name comes from the smartfolios we
  // already fetched, and decides whether the list renders as structured notes.
  const { data } = useGetRecommendedSmartfoliosQuery({
    page_size: 100,
    page_number: 1,
    struct_type: "MARKET",
  });

  useEffect(() => {
    if (!isHydrated) return;
    if (!market) {
      if (marketName) dispatch(setMarketName(undefined));
      return;
    }
    const name = findMarketName(data?.data, market);
    if (name && name !== marketName) dispatch(setMarketName(name));
  }, [isHydrated, market, marketName, data, dispatch]);
};
